import type { FileWithId } from 'src/types';
import type { BucketParamsObject } from 'src/types/s3';
import type { GridRowSelectionModel } from '@mui/x-data-grid';

import { useRef, useMemo, useState, useCallback } from 'react';

import Grid from '@mui/material/Grid2';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import { useCreateRun } from 'src/hooks/api/pipeline/useCreateRun';
import { useGetBucketById } from 'src/hooks/api/s3Buckets/useGetBucketById';
import { useGetAllBucketsS3 } from 'src/hooks/api/s3Buckets/useS3Integration';

import { LeftSide } from './FileBrowserComponent/LeftSide';
import { RightSide } from './FileBrowserComponent/RightSide';
import { FooterFolder } from './FileBrowserComponent/FooterFolder';
import { useChatHistoryContext } from '../hooks/useChatHistoryContext';

type FileBrowserDialogProps = {
  title: string;
  open: boolean;
  onClose: () => void;
};

// TODO: make this dialog as a generic component for file browser
function FileBrowserDialog({ title, open, onClose }: FileBrowserDialogProps) {
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);
  const selectedFilesRef = useRef<GridRowSelectionModel>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<FileWithId[]>([]);
  const [leftPagination, setLeftPagination] = useState({ page: 1, page_size: 10 });

  //Right side component params
  const [bucketParams, setBucketParams] = useState<BucketParamsObject>({ page: 1, page_size: 10, prefix: null });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPath, setCurrentPath] = useState<string>('');

  const { bucketDetails, isLoadingBucketDetails } = useGetBucketById(selectedBucket || "", bucketParams);
  const { bucketList, isLoadingBucketLIst } = useGetAllBucketsS3(leftPagination);

  const { createRun } = useCreateRun();
  const { setRunId } = useChatHistoryContext()


  // Memoize bucket click handler to prevent LeftSide re-renders
  const handleBucketClick = useCallback((_bucketName: string) => {
    setSelectedBucket(_bucketName);
    // Reset search and navigation state
    setSearchTerm('');
    setCurrentPath('');
    // Reset bucket params when bucket changes
    setBucketParams({ page: 1, page_size: 10, prefix: null });
  }, []);

  const onS3AudioFileUpload = useCallback((intendId: string, file: FileWithId) => {
    createRun(
      {
        intent_id: intendId ?? "",
        audio_file: file,
        mode: 'chat_with_audio',
      },
      {
        onSuccess: (runId) => {
          setRunId({ runId });
        },

      }
    );
    onClose();
  }, [createRun, setRunId, onClose]);

  // Memoize LeftSide props to prevent unnecessary re-renders
  const leftSideProps = useMemo(() => ({
    bucketList: bucketList ?? null,
    isLoadingBucketLIst,
    selectedBucket,
    handleBucketClick,
    pagination: leftPagination,
    setPagination: setLeftPagination,
  }), [bucketList, isLoadingBucketLIst, selectedBucket, handleBucketClick, leftPagination]);

  // Memoize RightSide props
  const rightSideProps = useMemo(() => ({
    selectedFilesRef,
    selectedBucket,
    bucketDetails,
    isLoadingBucketDetails,
    bucketParams,
    setBucketParams,
    selectedRowsData,
    setSelectedRowsData,
    searchTerm,
    setSearchTerm,
    currentPath,
    setCurrentPath,
    open,
  }), [
    selectedFilesRef,
    selectedBucket,
    bucketDetails,
    isLoadingBucketDetails,
    bucketParams,
    setBucketParams,
    selectedRowsData,
    setSelectedRowsData,
    searchTerm,
    setSearchTerm,
    currentPath,
    setCurrentPath,
    open
  ]);

  // Memoize footer props
  const footerProps = useMemo(() => ({
    selectedRowsData,
    onClose,
    onS3AudioFileUpload
  }), [selectedRowsData, onClose, onS3AudioFileUpload]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" >
      <DialogTitle sx={{ p: '16px 24px' }}>{title}</DialogTitle>
      <DialogContent>
        <Paper variant="outlined">
          <Grid container>
            <Grid size={4} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Stack>
                <Typography variant="subtitle1" p={2}>
                  Buckets
                </Typography>
                <Divider />
              </Stack>
              <LeftSide {...leftSideProps} />
            </Grid>
            <Grid size={8} p={2} sx={{ borderLeft: '1px solid #e0e0e0' }}>
              <RightSide {...rightSideProps} />
            </Grid>
          </Grid>
        </Paper>
      </DialogContent>
      <DialogActions>
        <FooterFolder {...footerProps} />
      </DialogActions>
    </Dialog>
  );
}

export default FileBrowserDialog;
